export interface IServices {
  cloudFunctions: string
  jobs: string
  jobsAutomation: string
  events: string
  registration: string
  applicationHistory: string
  content: string
  employer: string
  auth: string
  files: string
  messaging: string
  virtualDialogue: string
  notifications: string
  access: string
  sse: string
  product: string
  integration: string
  crm?: string
}

export interface IPlatformAdminServices {
  jobs: string
  jobsAutomation?: string
  auth: string
  user: string
  files: string
  registration: string
  customer: string
  notifications: string
  product: string
  reporting: string
  crm: string
  platformAdmin: string
}
export interface ICandidateServices extends IServices {
  candidate: string
  social: string
  community: string
  user: string
}
export interface IEmployerServices extends IServices {
  user: string
  jobsIntegration: string
  customer: string
  jobsAutomation: string
  jobsAssistant: string
  assistant: string
  candidatePool: string
  reporting: string
  integration: string
  platformAdmin: string
}

export type ServiceNames = keyof (IServices & ICandidateServices & IEmployerServices)

export interface ITenantConfig {
  appConfigBaseURL: string
  defaultTenant: string
}

export interface IEnvironment {
  production: boolean
  env: Env
  firebaseConfig: any
  services: IServices
  singleBackendServices: ServiceNames[]
  authentication: boolean
  themesEnabled: boolean
  v2ReCaptcha: string
  tenantId: string
  cdnCname: string
  basePlatformHost: string
  algoliaAppId: string
  version: string
  passwordRecoveryEmailTemplateId: string
  emailVerificationEmailTemplateId: string
  betaMessageId: string
  tenant: ITenantConfig
}

export interface ICandidateEnvironment extends IEnvironment {
  services: ICandidateServices
  employerEmailActionRoute: string
  candidateEmailSuccessRoute: string
  candidateBaseUrl: string
  appDownloadDynamicLink: string
  careersPageBaseLink: string
  smartBannerOptions: {
    appIcon: string
    appStoreUrl: string
    playStoreUrl: string
    ttl: string // this would keep banner closed for 24 hours
  }
}
export interface IEmployerEnvironment extends IEnvironment {
  services: IEmployerServices
  candidateExternalJobUrl: string
  employerBaseUrl: string
  careersPageBaseLink: string
  candidateEmailActionRoute: string
  employerEmailSuccessRoute: string
}

export interface ITalentEnvironment extends IEnvironment {
  services: IEmployerServices
  candidateExternalJobUrl: string
  careersPageBaseLink: string
  candidateEmailActionRoute: string
  employerEmailSuccessRoute: string
}

export interface IPlatformAdminEnvironment {
  singleBackendServices: ServiceNames[]
  production: boolean
  env: string
  firebaseConfig: any
  services: IPlatformAdminServices
  authentication: boolean
  themesEnabled: boolean
  v2ReCaptcha: string
  tenantId: string
  cdnCname: string
  algoliaAppId: string
  version: string
  basePlatformHost: string
  /**
   * Local-testing only: when set, the "View as customer" handoff opens this employer base URL
   * (e.g. http://localhost:4610) instead of the target customer's configured bootstrap URL, so the
   * full act-as flow can be exercised against a locally-served employer. Leave undefined everywhere
   * but local `environment.ts`.
   */
  employerActAsUrlOverride?: string
  /**
   * Regex (source string) the act-as handoff host must match before the admin app navigates a tab to
   * it. The backend returns the customer's bootstrap URL; this guards against a mis-set or tampered URL
   * pointing the tab (and the one-time code) at an unexpected host.
   */
  actAsAllowedHosts: string
}

export type Env = 'dev' | 'ci' | 'qa' | 'stage' | 'demo' | 'production'
export const PRODUCTION_ENV: 'production' = 'production'

export type ProjectId = 'cnect-dev' | 'cnect-ci' | 'cnect-qa-100' | 'cnect-stage-100' | 'cnect-demo-100' | 'cnect-prod-100'
export const envToProjectId: Record<Env, ProjectId> = {
  dev: 'cnect-dev',
  ci: 'cnect-ci',
  qa: 'cnect-qa-100',
  stage: 'cnect-stage-100',
  demo: 'cnect-demo-100',
  production: 'cnect-prod-100',
}
